import { useQuery } from "@tanstack/react-query";
import schedulerApi from "../../../services/api/SchedulerApiService";
import { useAuthStore } from "../../zustand";

/**
 * Custom hook for fetching and caching user exercise preferences
 *
 * Provides list of available exercise preferences/tags (e.g., "Back", "Shoulder", "Core", "Random").
 * Uses TanStack Query as the single source of truth - no redundant Zustand caching.
 * Data is cached per language to enable instant language switching without API refetch.
 *
 * Cache Strategy:
 * - Query key format: ['preferences', email, preferredLanguage]
 * - Separate cache entries per language ensure instant language switching
 * - staleTime: 10 minutes (preferences change rarely)
 * - gcTime: 15 minutes (keep longer than homeData since rarely changes)
 * - Do NOT refetch on mount or tab focus (preferences are stable)
 * - Refetch only on network reconnect
 *
 * Special Handling:
 * - Extracts "Random" preference ID for special handling in exercise form logic
 * - Random preference allows selecting exercises randomly instead of by type
 *
 * @returns {Object} Query result object
 * @returns {Object} result.data - {rawPreferences: [], randomPreferenceId: string|null}
 * @returns {boolean} result.isLoading - True on first load (no cached data)
 * @returns {boolean} result.isFetching - True while any fetch is in progress
 * @returns {Error} result.error - Error object if fetch failed
 * @returns {Function} result.refetch - Manual refetch function
 */
export const usePreferencesQuery = () => {
  // Get current user email from auth store (used as cache key dimension)
  const email = useAuthStore((state) => state.user.email);
  // Get user's language preference from auth store (used as cache key dimension for instant switching)
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);

  /**
   * TanStack Query hook for managing preferences fetching and caching
   * Query is disabled until user is authenticated (email exists)
   */
  const query = useQuery({
    // Cache key dimensions: separate caches per user email and language
    // This enables instant language switching by selecting a different cache entry
    queryKey: ["preferences", email, preferredLanguage],

    /**
     * Query function: fetch preferences from API and extract special "Random" ID
     * @async
     * @returns {Object} Structured data with preferences list and random preference ID
     */
    queryFn: async () => {
      // Fetch list of available exercise preferences (tags) from API
      const response = await schedulerApi.getExercisePreferences(
        preferredLanguage
      );

      // Extract preferences array from response (or empty array if missing)
      const rawPreferences = response.preferences || [];

      // Find the "Random" preference object for special handling in exercise form logic
      // Random preference allows users to select exercises randomly by category
      // instead of selecting specific preferences
      const randomPreference = rawPreferences.find(
        (pref) => pref.name === "Random"
      );

      // Return structured data containing full preferences list and random preference ID
      const preferencesData = {
        rawPreferences: rawPreferences, // Full list of preference objects for form dropdowns
        randomPreferenceId: randomPreference?.id || null, // ID of Random preference or null if not found
      };

      return preferencesData;
    },

    // Only enable query when user is authenticated (email available from auth store)
    enabled: !!email,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 48 * 60 * 60 * 1000, // 48 hours
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
    // Retry failed requests up to 3 times with exponential backoff
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Structural sharing false for performance (preferences array doesn't need deep equality)
    structuralSharing: false,
  });

  // Return subset of query state useful for UI components
  return {
    data: query.data, // Structured data: {rawPreferences, randomPreferenceId}
    isLoading: query.isLoading, // True during initial load (no cached data exists)
    isFetching: query.isFetching, // True while any fetch is in progress (including background refetch)
    error: query.error, // Error object if fetch failed, null if successful
    refetch: query.refetch, // Function to manually trigger refetch
  };
};
